<script setup lang="ts">
import { useAppStore } from '@/store/app/app'
import { useCityStore } from '@/store/city/city'

import {
  HttpStatusCode,
  NotFoundError,
  ServerError,
  Success,
} from '@/api/results'

import PaymentAttentions from '@/components/Payment/PaymentAttentions.vue'
import PaymentInfo from '@/components/Payment/PaymentInfo.vue'
import PaymentTypes from '@/components/Payment/PaymentTypes.vue'
import PaymentWarnings from '@/components/Payment/PaymentWarnings.vue'

const appStore = useAppStore()

const {
  cityInfo: { domain },
} = useCityStore()

usePopupFetch()

const { data: page, error } = await useAsyncData(async (nuxtApp) => {
  const result = await nuxtApp?.$apiService.site.fetchPaymentPage()

  if (result instanceof NotFoundError) {
    createError({
      statusCode: HttpStatusCode.NOT_FOUND,
    })
  }

  if (result instanceof ServerError) {
    throw createError({
      statusCode: HttpStatusCode.INTERNAL_SERVER_ERROR,
    })
  }

  if (result instanceof Success) {
    return result.data
  }
})

if (error.value) showError(error.value)

const breadcrumbs = computed(() => page.value?.breadcrumbs || [])

const isWebview = computed(() => appStore.isWebview)

const showBreadcrumb = computed(
  () => breadcrumbs.value?.length !== 0 && !isWebview.value,
)
</script>

<template>
  <div class="payment-page">
    <app-head
      :title="page?.seo.seo_title"
      :seo="{
        title: page?.seo?.seo_title,
        description: page?.seo?.seo_description,
        keywords: page?.seo?.seo_keywords,
      }"
    />

    <div class="payment-page__wrapper">
      <the-breadcrumbs
        v-if="showBreadcrumb"
        class="payment-page__breadcrumbs"
        :breadcrumbs="breadcrumbs"
        :domain="domain"
      />

      <h1 class="payment-page__title">
        {{ page?.page_title }}
      </h1>

      <div class="payment-page__types">
        <payment-types />
      </div>

      <div class="payment-page__attentions">
        <payment-attentions :page="page" />
      </div>

      <div class="payment-page__warnings">
        <payment-warnings :page="page" />
      </div>

      <div v-if="page?.howto_payment" class="payment-page__info">
        <payment-info :text="page?.howto_payment" />
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.payment-page {
  padding-top: 15px;
  padding-bottom: 50px;
  @include media('>=tablet') {
    padding-top: 25px;
  }
  @include media('>=desktop-mini') {
    padding-top: 50px;
    padding-bottom: 120px;
  }

  &__wrapper {
    max-width: 960px;
    margin: 0 auto;
  }

  &__breadcrumbs {
    margin-bottom: 20px;
  }

  &__title {
    margin-bottom: 20px;
    font-size: 18px;
    font-weight: 800;
    color: var(--ui-text-primary);
    @include media('>=tablet') {
      margin-bottom: 30px;
      font-size: 24px;
    }
    @include media('>=desktop-mini') {
      margin-bottom: 50px;
      font-size: 28px;
    }
  }

  &__types {
    margin-bottom: 30px;
    @include media('>=desktop-mini') {
      margin-bottom: 70px;
    }
  }

  &__attentions {
    margin-bottom: 40px;
    @include media('>=desktop-mini') {
      margin-bottom: 65px;
    }
  }

  &__warnings {
    margin-bottom: 40px;
    @include media('>=desktop-mini') {
      margin-bottom: 65px;
    }
  }
}
</style>
